import React, { useEffect, useState } from 'react';
import { Camera, Award, Heart, Users } from 'lucide-react';
import { photographerAPI } from '../services/api';

const About = () => {
  const [photographerInfo, setPhotographerInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchPhotographerInfo();
  }, []);

  const fetchPhotographerInfo = async () => {
    try {
      const response = await photographerAPI.getInfo();
      setPhotographerInfo(response.info);
    } catch (error) {
      console.error('Error fetching photographer info:', error);
    } finally {
      setLoading(false);
    }
  };

  // Only include stats that have meaningful values (not empty, not "0", not just whitespace)
  const hasValue = (val) => {
    if (!val) return false;
    const trimmed = val.toString().trim();
    return trimmed !== '' && trimmed !== '0';
  };

  const allStats = [
    { icon: Camera, label: 'Shootings', value: photographerInfo?.stat_shootings },
    { icon: Award, label: 'Auszeichnungen', value: photographerInfo?.stat_awards },
    { icon: Heart, label: 'Zufriedene Kunden', value: photographerInfo?.stat_clients },
    { icon: Users, label: 'Jahre Erfahrung', value: photographerInfo?.stat_experience }
  ];
  
  // Filter out stats without meaningful values
  const stats = allStats.filter(stat => hasValue(stat.value));

  if (loading || !photographerInfo) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Camera size={48} className="mx-auto mb-4 text-gray-400 animate-pulse" />
          <p className="text-gray-600">Wird geladen...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="section-spacing-large bg-gray-50">
        <div className="photo-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="fade-in-up">
              <h1 className="hero-title mb-6">Ueber Mich</h1>
              <p className="subtitle mb-8">
                Hallo, ich bin {photographerInfo.name}
              </p>
              <div className="space-y-4 body-text text-gray-600">
                <p>
                  {photographerInfo.bio}
                </p>
              </div>
            </div>
            <div className="fade-in-up" style={{ animationDelay: '0.2s' }}>
              <img 
                src={photographerInfo.about_image_url || "https://images.unsplash.com/photo-1554048612-b6a482bc67e5?w=800&q=80"} 
                alt={`${photographerInfo.name} Photographer`}
                className="w-full h-[600px] object-cover rounded-lg shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section - only show if there are stats with values */}
      {stats.length > 0 && (
        <section className="section-spacing bg-white">
          <div className="photo-container">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {stats.map((stat, index) => {
                const Icon = stat.icon;
                return (
                  <div 
                    key={stat.label} 
                    className="text-center fade-in-up"
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <div className="w-16 h-16 bg-primary text-white rounded-full flex items-center justify-center mb-4 mx-auto">
                      <Icon size={28} />
                    </div>
                    <p className="text-3xl font-bold text-primary mb-2">{stat.value}</p>
                    <p className="text-sm text-gray-600 uppercase tracking-wider">{stat.label}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Philosophy Section */}
      {photographerInfo.philosophy_text && (
        <section className="section-spacing bg-gray-50">
          <div className="photo-container">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="section-title">Meine Philosophie</h2>
              <div className="space-y-6 body-text text-gray-600">
                <div className="whitespace-pre-wrap">
                  {photographerInfo.philosophy_text}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Equipment Section */}
      {(photographerInfo.equipment_cameras || photographerInfo.equipment_lenses || photographerInfo.equipment_accessories) && (
        <section className="section-spacing bg-white">
          <div className="photo-container">
            <h2 className="section-title text-center">Professionelle Ausruestung</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {photographerInfo.equipment_cameras && (
                <div className="bg-gray-50 p-8 rounded-lg">
                  <h3 className="font-heading text-xl font-semibold mb-4">Kameras</h3>
                  <ul className="space-y-2 body-text text-gray-600">
                    {photographerInfo.equipment_cameras.split('\n').map((item, i) => (
                      item.trim() && <li key={i}>• {item.trim()}</li>
                    ))}
                  </ul>
                </div>
              )}
              {photographerInfo.equipment_lenses && (
                <div className="bg-gray-50 p-8 rounded-lg">
                  <h3 className="font-heading text-xl font-semibold mb-4">Objektive</h3>
                  <ul className="space-y-2 body-text text-gray-600">
                    {photographerInfo.equipment_lenses.split('\n').map((item, i) => (
                      item.trim() && <li key={i}>• {item.trim()}</li>
                    ))}
                  </ul>
                </div>
              )}
              {photographerInfo.equipment_accessories && (
                <div className="bg-gray-50 p-8 rounded-lg">
                  <h3 className="font-heading text-xl font-semibold mb-4">Zubehoer</h3>
                  <ul className="space-y-2 body-text text-gray-600">
                    {photographerInfo.equipment_accessories.split('\n').map((item, i) => (
                      item.trim() && <li key={i}>• {item.trim()}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default About;
